/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty, getJob} from './EditPipelinePage.slices.utils'

export const setJobOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
    value: string
  }>
> = (state, action) => {
  const {pipelineId, jobId, savedName, value} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job['output-parameters'] ??= {}
    job['output-parameters'][savedName] = value
  }
}

export const renameJobOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    name: string
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, savedName, name} = action.payload

  if (name !== savedName) {
    state[pipelineId] ??= {}
    state[pipelineId]!.renamed ??= {}
    state[pipelineId]!.renamed!.jobOutputParameters ??= {}
    state[pipelineId]!.renamed!.jobOutputParameters![savedName] = name
  } else if (state[pipelineId]?.renamed?.jobOutputParameters?.[savedName] != null) {
    delete state[pipelineId]!.renamed!.jobOutputParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'jobOutputParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  }
}

export const deleteJobOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, jobId, savedName} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (state[pipelineId]?.added?.jobOutputParameters?.[savedName]) {
    delete job?.['output-parameters']?.[savedName]
    delete state[pipelineId]!.added!.jobOutputParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.added, 'jobOutputParameters') &&
      deleteIfEmpty(state[pipelineId], 'added')
    delete state[pipelineId]!.renamed!.jobOutputParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'jobOutputParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  } else {
    state[pipelineId] ??= {}
    state[pipelineId]!.deleted ??= {}
    state[pipelineId]!.deleted!.jobOutputParameters ??= []
    state[pipelineId]!.deleted!.jobOutputParameters!.push(savedName)
  }
}

export const restoreJobOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; savedName: string}>
> = (state, action) => {
  const {pipelineId, savedName} = action.payload
  const {deleted} = state[pipelineId] ?? {}

  if (deleted?.jobOutputParameters?.includes(savedName)) {
    deleted.jobOutputParameters = deleted?.jobOutputParameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'jobOutputParameters') && deleteIfEmpty(state[pipelineId], 'deleted')
  }
}

export const addJobOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string}>
> = (state, action) => {
  const {pipelineId, jobId} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job['output-parameters'] ??= {}
    const key = generateFreeName({
      getName: iteration => `output_parameter_${jobId}_${iteration}`,
      usedNames: Object.keys(job['output-parameters']),
      errorMessage: 'Cannot generate unique parameter key',
    })
    job['output-parameters'][key] = ''
    renameJobOutputParameter(state, {type: '', payload: {pipelineId, name: '', savedName: key}})
    state[pipelineId]!.added ??= {}
    state[pipelineId]!.added!.jobOutputParameters ??= {}
    state[pipelineId]!.added!.jobOutputParameters![key] = true
  }
}

export const clearAddedJobOutputParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string}>
> = (state, action) => {
  const {pipelineId, jobId} = action.payload
  const {added} = state[pipelineId] ?? {}
  const job = getJob(state, pipelineId, jobId)

  if (added?.jobOutputParameters != null && job != null) {
    Object.keys(added.jobOutputParameters).forEach(key => {
      delete job['output-parameters']?.[key]
    })
    delete added.jobOutputParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const applyAddedJobOutputParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<string>
> = (state, action) => {
  const pipelineId = action.payload
  const {added} = state[pipelineId] ?? {}

  if (added?.jobOutputParameters != null) {
    delete added.jobOutputParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}
